import React from "react";
import { useSelector } from "react-redux";
import { CableIcon, MessagesSquareIcon, PocketKnifeIcon, UsersIcon } from "lucide-react";

import Message from "../../i18n/Message";
import { authSelectors } from "../auth/store/authReducer";
import {
  subscriptionLiveChatMessageLimits,
  subscriptionPlaygroundLimits,
  subscriptionToolLimits,
  subscriptionUserLimits,
} from "../shared/pricingLimits";

// 999 is the "effectively unlimited" sentinel used for the Personalized/Enterprise plan.
// Match it exactly — real caps can legitimately exceed it (e.g. Pro allows 1000 Live Chat
// messages), and those must render as their number, not as ∞.
const format = (n) => {
  if (n === undefined || n === null) return "—";
  if (n === 999) return "∞"; // ∞
  return Number(n).toLocaleString();
};

/**
 * Shows the current plan's key allowances (playgrounds, users, MCP tools, Live Chat messages),
 * replacing the old token-usage meter on the Subscription page. Values are the backend-resolved
 * per-subscription limits (subscription.limits), falling back to the pricingLimits plan mirror when
 * the server didn't ship them — so an enterprise customer sees their actual negotiated numbers.
 */
const PlanLimits = () => {
  const user = useSelector(authSelectors.getUser);
  const sub = user?.subscription;
  const plan = sub?.plan;
  if (!plan) return null;

  const L = sub?.limits;
  const messages =
    L?.monthlyMessages ?? subscriptionLiveChatMessageLimits[plan] ?? 0;

  const items = [
    {
      key: "playgrounds",
      icon: <CableIcon size={22} />,
      value: format(L?.playgrounds ?? subscriptionPlaygroundLimits[plan]),
      labelId: "plan_limit_playgrounds",
    },
    {
      key: "users",
      icon: <UsersIcon size={22} />,
      value: format(L?.users ?? subscriptionUserLimits[plan]),
      labelId: "plan_limit_users",
    },
    {
      key: "tools",
      icon: <PocketKnifeIcon size={22} />,
      value: format(L?.tools ?? subscriptionToolLimits[plan]),
      labelId: "plan_limit_tools",
    },
    {
      key: "messages",
      icon: <MessagesSquareIcon size={22} />,
      // Lite has no Live Chat -> show a dash rather than "0".
      value: messages > 0 ? format(messages) : "—",
      labelId: "plan_limit_messages",
    },
  ];

  return (
    <div className="limits_panel plan_limits_panel">
      <h3>
        <Message id="plan_limits_title" />
      </h3>
      <div className="plan_limits_grid">
        {items.map((it) => (
          <div className="plan_limit_item" key={it.key}>
            <div className="plan_limit_icon">{it.icon}</div>
            <div className="plan_limit_value">{it.value}</div>
            <div className="plan_limit_label">
              <Message id={it.labelId} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanLimits;
